/**
 * A custom domain (an owner's own domain, like app.yourcompany.com), on an
 * app's settings screen. Additive to the Web address section above it: the SV
 * Cloud address never goes away, this is a second way to reach the same app.
 *
 * WHAT THIS SCREEN HAS TO GET ACROSS:
 *
 * 1. You add two DNS records at your own provider (a CNAME and a TXT), and then
 *    it takes a few minutes for the certificate to be issued. So the records
 *    are shown as copyable text, and the status says plainly where a domain is
 *    while it is not live yet, rather than only "pending".
 *
 * 2. It is not instant and not something we can do for you: the records live at
 *    your registrar. The one action here that reaches the server is "Check
 *    status", which re-asks the certificate authority.
 *
 * 3. The records are a TABLE, in the shape a DNS provider's own form asks for:
 *    type, name, content, proxy. The owner is copying values into a grid of
 *    fields somewhere else, and the earlier shape (numbered blocks of labelled
 *    pairs) made them count the pairs to work out how many records they needed.
 *
 * 4. Every record belongs to one action. Cloudflare mints the DV-certificate
 *    TXT asynchronously, so cloud-api's add already waits a beat for it, and
 *    when it still is not there the TXT renders as a shadow that fills itself
 *    in. What must never happen again is the screen asking for records it is
 *    not showing, with a button the owner has to guess is how you fetch them.
 *    There can be more than one TXT, because Cloudflare returns one token per
 *    name on the certificate, so they render as a list and never as "the"
 *    record.
 *
 * Every rule behind it lives on the server (apps/cloud's
 * services/custom-domains.ts). This file renders the answers and never
 * re-derives them. Production only, matching StagingSection's gate in Settings.
 */

import { useEffect, useRef, useState } from 'react';

import { Button } from '../primitives/Button';
import { Field } from '../primitives/Field';
import { Table, type TableColumn } from '../primitives/Table';
import { Lamp, type LampShape, type LampTone } from './StatusPill';
import { CopyField } from './CopyField';
import { Notice } from './Notice';
import { SectionPanel } from './SectionPanel';
import { Skeleton } from './Skeleton';

import {
  addCustomDomain,
  type CustomDomain,
  listCustomDomains,
  removeCustomDomain,
  verifyCustomDomain,
} from '../../lib/api';
import { captureEvent } from '../../lib/analytics/posthog';
import { useFormTracker } from '../../lib/analytics/use-form-tracker';
import { relativeName } from '../../lib/dns-name';
import { downloadText } from '../../lib/export';
import { ApiError } from '../../lib/http';
import { useAsync } from '../../lib/hooks/use-async';
import { t } from '../../lib/i18n/t';
import type { Project } from '../../lib/types';
import { toZoneFile, zoneFileName, zoneFileReady } from '../../lib/zone-file';

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

/**
 * The client half of the missing-TXT problem. cloud-api waits for Cloudflare to
 * mint the certificate token before it answers the add, and usually comes back
 * with both records; this covers the mint that takes longer than that. The
 * shadow is what tells the owner a second record is coming, so the screen never
 * asks for two records while showing one.
 *
 * Bounded: past TXT_POLL_MAX the screen stops asking and says so, rather than
 * hitting Cloudflare every few seconds for the rest of the afternoon on a
 * hostname whose certificate is genuinely stuck.
 */
const TXT_POLL_MS = 3000;
const TXT_POLL_MAX = 10;

interface Props {
  project: Project;
}

/**
 * Cloudflare's own words for the one state every new domain starts in: the
 * hostname does not resolve here yet, because nobody has added the records
 * yet. It is not a failure and there is nothing to act on beyond the
 * instructions already on screen, so it must not displace them. Matched
 * loosely, since the exact phrasing is Cloudflare's to change.
 */
const DNS_NOT_POINTED_YET =
  /does not (?:cname|point) to this zone|not routed to cloudflare|no (?:a|aaaa|cname)? ?records? found/i;

/** Hostname states nothing on this screen can move. Cloudflare has stopped. */
const DEAD_STATUSES = ['blocked', 'moved', 'deleted'];

/**
 * Plain-language state for a domain, from Cloudflare's own status fields.
 *
 * The ordering is the point. Before this, any non-null `error` won, which meant
 * a domain added thirty seconds ago greeted its owner with "custom hostname
 * does not CNAME to this zone." instead of the sentence written for exactly
 * that moment. Cloudflare's raw text is kept for the cases it is the only
 * actionable thing: a genuinely stuck certificate, or a hostname Cloudflare
 * will not serve at all.
 */
function statusLine(domain: CustomDomain): {
  text: string;
  tone: 'ok' | 'wait' | 'attention';
  /** The lamp beside the words. Shape AND colour, never colour alone. */
  lamp: { shape: LampShape; tone: LampTone };
} {
  // Three lamps, matching what a person already expects from a DNS dashboard:
  // green once it serves, red when something is wrong, blue while it waits on
  // the owner's DNS or a certificate authority.
  const GREEN = { shape: 'disc', tone: 'live' } as const;
  const RED = { shape: 'barred', tone: 'down' } as const;
  const BLUE = { shape: 'arc', tone: 'pending' } as const;

  if (domain.verified) return { text: t('Live'), tone: 'ok', lamp: GREEN };

  if (DEAD_STATUSES.includes(domain.status)) {
    return {
      text: domain.error ?? t('Cloudflare will not serve this domain. Remove it and add it again.'),
      tone: 'attention',
      lamp: RED,
    };
  }

  const waitingOnDns = domain.error !== null && DNS_NOT_POINTED_YET.test(domain.error);
  if (domain.error !== null && !waitingOnDns) {
    return { text: domain.error, tone: 'attention', lamp: RED };
  }
  if (!waitingOnDns && domain.sslStatus !== null && domain.sslStatus !== 'pending_validation') {
    return {
      text: t('Issuing your certificate. This can take a few minutes.'),
      tone: 'wait',
      lamp: BLUE,
    };
  }
  return {
    text: t('Waiting for your DNS records. Add the ones below, then check again.'),
    tone: 'wait',
    lamp: BLUE,
  };
}

/**
 * One row per record the owner has to create, in the shape a DNS provider's own
 * form asks for: type, name, content, and whether it is proxied.
 *
 * It is a table because it is one: the owner is copying values into a grid of
 * fields somewhere else, and a numbered list of labelled pairs made them count
 * the pairs to find out how many records they needed. A screen reader gets the
 * column headers for free, which is what the earlier shape could not give.
 *
 * The NAME column carries both forms. Providers disagree about whether the zone
 * is appended for you, and pasting the wrong one produces
 * `gallery.customer.com.customer.com`, which resolves to nothing and reads as a
 * typo nobody made. Showing both costs one line and removes the guess; see
 * lib/dns-name.ts for why the short form is a heuristic and is allowed to be.
 */
interface DnsRow {
  key: string;
  index: number;
  type: 'CNAME' | 'TXT';
  name: string | null;
  content: string | null;
  proxy: string;
}

function DnsRecordsTable({
  domain,
  pollExhausted,
  projectSlug,
}: {
  domain: CustomDomain;
  pollExhausted: boolean;
  projectSlug: string;
}) {
  const rows: DnsRow[] = [
    {
      key: 'cname',
      index: 1,
      type: 'CNAME',
      name: domain.cname.name,
      content: domain.cname.value,
      // Only meaningful when the owner's own DNS is Cloudflare, but it is the
      // one setting that silently breaks the whole thing: a proxied record
      // never reaches our zone's routing. Kept short so the column does not
      // wrap to four lines; the note under the table names the grey cloud.
      proxy: t('DNS only'),
    },
  ];

  if (domain.txtRecords.length > 0) {
    // One row per token. Cloudflare returns one per name on the certificate and
    // every one has to be added; a missing token is a certificate that never
    // issues, indistinguishable from a record nobody added yet.
    domain.txtRecords.forEach((record, i) => {
      rows.push({
        key: `txt:${record.name}:${record.value}`,
        index: rows.length + 1,
        type: 'TXT',
        name: record.name,
        content: record.value,
        proxy: t('n/a'),
      });
      void i;
    });
  } else {
    // The placeholder row. The table says two records are needed from the first
    // paint, even though Cloudflare has not minted the second one yet, so the
    // owner is never told to add records the screen is not showing.
    rows.push({
      key: 'txt-pending',
      index: 2,
      type: 'TXT',
      name: null,
      content: null,
      proxy: t('n/a'),
    });
  }

  // Values are truncated in the MIDDLE, not the end, and only on screen: the
  // copy button always takes the whole string. A DV token is not read, it is
  // copied, and an untruncated one pushes the Proxy column off the table. The
  // middle is what goes, so both ends stay comparable against what the owner
  // pasted, and the full value is on the element's title.
  const cell = (value: string | null, label: string, kind: string, truncateAt: number) => {
    if (value === null) {
      return (
        <Skeleton
          shape="field"
          count={1}
          label={t('Waiting for your certificate record from Cloudflare')}
        />
      );
    }
    return (
      <CopyField
        label={label}
        value={value}
        size="inline"
        truncateAt={truncateAt}
        onCopied={() => captureEvent('custom_domain_record_copied', { record: kind })}
      />
    );
  };

  const columns: TableColumn<DnsRow>[] = [
    { key: 'n', header: '#', width: '2.5rem', render: (row) => String(row.index) },
    { key: 'type', header: t('Type'), width: '5.5rem', render: (row) => row.type },
    {
      key: 'name',
      header: t('Name field'),
      render: (row) => (
        <div className="set__cell">
          {cell(
            row.name === null ? null : relativeName(row.name),
            t('Name'),
            `${row.type}:name`,
            24,
          )}
          {row.name !== null ? <span className="set__cell-alt mono">{row.name}</span> : null}
        </div>
      ),
    },
    {
      key: 'content',
      header: t('Content field'),
      render: (row) => cell(row.content, t('Value'), `${row.type}:value`, 26),
    },
    { key: 'proxy', header: t('Proxy'), width: '7rem', render: (row) => row.proxy },
  ];

  return (
    <>
      <Table
        caption={t('DNS records to add for this domain')}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.key}
        stackOnNarrow
      />
      {/* The same records as one importable file, so an owner whose provider
          can import a zone file never copies a value by hand. Offered only
          once the file would be complete: a file missing the certificate
          token imports cleanly and leaves the certificate stuck, which is
          worse than no file. Built in the browser from the rows above; see
          lib/zone-file.ts for why it is valid at every provider at once. */}
      {zoneFileReady(domain) ? (
        <div className="set__control">
          <p className="set__say">
            {t(
              'Skip the typing: download these records as one file and import it at your DNS provider. Cloudflare, GoDaddy and Route 53 can import it. If yours cannot, add the records by hand.',
            )}
          </p>
          <div className="set__actions">
            <Button
              variant="outline"
              onClick={() => {
                downloadText(zoneFileName(domain), toZoneFile(domain), 'text/plain');
                captureEvent('custom_domain_dns_file_downloaded', {
                  project_slug: projectSlug,
                  txt_records: domain.txtRecords.length,
                });
              }}
            >
              {t('Download DNS file')}
            </Button>
          </div>
        </div>
      ) : null}
      <p className="set__say">
        {t(
          'Each name is shown two ways: the short form on top for providers that add your domain for you, and the full form under it for providers that do not.',
        )}
      </p>
      <p className="set__say">
        {t('On Cloudflare, DNS only is the grey cloud. A proxied record will not work.')}
      </p>
      {domain.txtRecords.length === 0 ? (
        <p className="set__say">
          {pollExhausted
            ? t(
                'Your certificate record is taking longer than usual. Add the CNAME now, and use Check status for the rest.',
              )
            : t(
                'Your certificate record is on its way. It appears here on its own, usually within a few seconds.',
              )}
        </p>
      ) : null}
    </>
  );
}

export function CustomDomainSection({ project }: Props) {
  const domains = useAsync(() => listCustomDomains(project.id), [project.id]);
  const [hostname, setHostname] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useFormTracker({
    formId: 'custom_domain_add',
    formName: 'Add a custom domain',
    totalFields: 1,
    projectSlug: project.slug,
  });

  // Domains still missing their certificate record, as a stable string so the
  // poll below re-arms on what actually changed and not on every refetch's new
  // array identity. `tick` is what drives the next round after one lands.
  // A Cloudflare custom hostname is billed monthly, so apps get a small cap.
  // The cap itself lives on the server (services/custom-domains.ts's
  // CUSTOM_DOMAINS_PER_PROJECT) and arrives with the list, so the client never
  // hardcodes the number; it only compares against it.
  const held = domains.data?.domains ?? [];
  const limit = domains.data?.limit ?? 1;
  const atLimit = held.length >= limit;

  const awaitingTxt = held
    .filter((domain) => !domain.verified && domain.txtRecords.length === 0)
    .map((domain) => domain.id);
  const awaitingTxtKey = awaitingTxt.join(',');
  const txtAttempts = useRef(new Map<string, number>());
  const [txtTick, setTxtTick] = useState(0);
  const refetch = domains.refetch;

  useEffect(() => {
    if (awaitingTxtKey === '') {
      // Everything that was waiting has its record now. Report how long that
      // took in polls, then forget, so a later add starts with a clean budget.
      for (const [id, attempts] of txtAttempts.current) {
        if (attempts > 0) captureEvent('custom_domain_txt_arrived', { attempts, domain_id: id });
      }
      txtAttempts.current.clear();
      return;
    }
    if (busy) return;

    const due = awaitingTxtKey
      .split(',')
      .filter((id) => (txtAttempts.current.get(id) ?? 0) < TXT_POLL_MAX);
    if (due.length === 0) return;

    const timer = window.setTimeout(() => {
      void (async () => {
        for (const id of due) {
          const attempts = (txtAttempts.current.get(id) ?? 0) + 1;
          txtAttempts.current.set(id, attempts);
          try {
            await verifyCustomDomain(project.id, id);
          } catch {
            // A failed read is not worth a banner: the next tick tries again,
            // and giving up says so on the screen.
          }
          if (attempts === TXT_POLL_MAX) {
            captureEvent('custom_domain_txt_poll_exhausted', { domain_id: id });
          }
        }
        refetch();
        setTxtTick((n) => n + 1);
      })();
    }, TXT_POLL_MS);

    return () => window.clearTimeout(timer);
  }, [awaitingTxtKey, busy, project.id, refetch, txtTick]);

  async function add(): Promise<void> {
    const value = hostname.trim();
    if (value === '') return;
    setBusy(true);
    setError(null);
    form.trackSubmit({ hostname_length: value.length });
    captureEvent('custom_domain_add_submitted', {
      project_slug: project.slug,
      domains_held: held.length,
      domain_limit: limit,
    });
    try {
      await addCustomDomain(project.id, value);
      form.trackSubmitSuccess();
      captureEvent('custom_domain_add_succeeded', {
        project_slug: project.slug,
        domains_held: held.length + 1,
        domain_limit: limit,
      });
      setHostname('');
      domains.refetch();
    } catch (err: unknown) {
      const message = err instanceof ApiError ? err.message : t('That did not go through. Try again.');
      const code = err instanceof ApiError ? err.code : 'unknown';
      form.trackSubmitError(code, message);
      captureEvent('custom_domain_add_failed', { project_slug: project.slug, error_code: code });
      setError(message);
    } finally {
      setBusy(false);
    }
  }

  async function check(domain: CustomDomain): Promise<void> {
    setBusy(true);
    setError(null);
    captureEvent('custom_domain_verify_clicked', { project_slug: project.slug });
    try {
      await verifyCustomDomain(project.id, domain.id);
      domains.refetch();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : t('That did not go through. Try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(domain: CustomDomain): Promise<void> {
    setBusy(true);
    setError(null);
    captureEvent('custom_domain_removed', { project_slug: project.slug });
    try {
      await removeCustomDomain(project.id, domain.id);
      domains.refetch();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : t('That did not go through. Try again.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <SectionPanel
      title={limit > 1 ? t('Custom domains') : t('Custom domain')}
      note={
        limit > 1
          ? t(
              'Serve your app on up to {limit} domains you own, like yourcompany.com and www.yourcompany.com. Your SV Cloud address keeps working too.',
              { limit },
            )
          : t(
              'Serve your app on a domain you own, like app.yourcompany.com. Your SV Cloud address keeps working too.',
            )
      }
    >
      <div className="set__control">
        {error ? <Notice tone="attention">{error}</Notice> : null}

        {domains.isLoading ? (
          <Skeleton count={2} label={t('Loading your custom domains')} />
        ) : (
          held.map((domain) => {
            const status = statusLine(domain);
            return (
              <div key={domain.id} className="set__domain">
                <p className="set__say">
                  <strong>{domain.hostname}</strong>
                </p>
                {/* One line for every state, rather than a Notice for the bad
                    one: a domain is always in exactly one state and swapping
                    the whole element for it moved the words around the screen.
                    Notice's `attention` is the amber failure tone, which is
                    not what a dead domain is. The lamp carries SHAPE as well
                    as colour and the words are never dropped, so turning every
                    colour off leaves this readable. */}
                <p
                  className="set__status"
                  data-tone={status.tone}
                  role="status"
                  aria-live="polite"
                >
                  <Lamp shape={status.lamp.shape} tone={status.lamp.tone} size="md" />
                  <span>{status.text}</span>
                </p>

                {/* Records are shown while a domain can still become live, so
                    NOT for a dead one: telling somebody to add DNS for a
                    hostname Cloudflare has stopped serving sends them to do
                    work that cannot help. Verified domains need no records
                    either, the whole point being that they are already there. */}
                {!domain.verified && !DEAD_STATUSES.includes(domain.status) ? (
                  <>
                    <p className="set__say">
                      {t('Add these records at your DNS provider:')}
                    </p>
                    <DnsRecordsTable
                      domain={domain}
                      pollExhausted={(txtAttempts.current.get(domain.id) ?? 0) >= TXT_POLL_MAX}
                      projectSlug={project.slug}
                    />
                  </>
                ) : null}

                <div className="set__actions">
                  <Button variant="outline" disabled={busy} onClick={() => check(domain)}>
                    {t('Check status')}
                  </Button>
                  <Button variant="outline" disabled={busy} onClick={() => remove(domain)}>
                    {t('Remove')}
                  </Button>
                </div>
              </div>
            );
          })
        )}

        {USE_MOCK ? (
          <p className="set__say">{t('Adding a custom domain is not part of this demo.')}</p>
        ) : atLimit ? (
          // No field at all, rather than a field whose only possible outcome is
          // a refusal. The server is still the authority (it refuses with
          // CUSTOM_DOMAIN_LIMIT either way); this is about not offering
          // somebody an action that cannot succeed.
          <p className="set__say">
            {limit > 1
              ? t('{limit} custom domains per app. Remove one to use a different address.', {
                  limit,
                })
              : t('One custom domain per app. Remove this one to use a different address.')}
          </p>
        ) : (
          <div className="set__control">
            <Field
              label={t('Domain')}
              value={hostname}
              placeholder="app.yourcompany.com"
              onChange={(event) => {
                setHostname(event.target.value);
                form.onFieldChange('hostname', event.target.value);
              }}
              onFocus={() => form.onFieldFocus('hostname')}
              onBlur={() => form.onFieldBlur('hostname')}
              disabled={busy}
            />
            <div className="set__actions">
              <Button
                variant="outline"
                loading={busy}
                disabled={hostname.trim() === ''}
                onClick={add}
              >
                {t('Add domain')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </SectionPanel>
  );
}
